import React, { useState } from 'react';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import { translate } from '../../../services/translate';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { CitizenParamList } from '../../../typings/Params';
import { RouteProp, useRoute } from '@react-navigation/native';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { Pagination } from '../../../components/Pagination';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import { citizenService } from '../../../services/central-api/citizen';
import MyDataTable from '../../../components/MyDataTable';
import CustomButton from '../../../components/CustomButton';
import CitizenBlockReasonModal from './CitizenBlockReasonModal';
import DropdownButton from '../../../components/DropdownButton';
import DeleteModal from '../../../components/DeleteModal';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import Filter from './Filters';
import { RootNavigation } from '../../../Routes';

export default function CitizenBlockReasons({ navigation }: { navigation: RootNavigation; }) {
    const { user: authenticatedUser } = useAuth();
    const route = useRoute<RouteProp<CitizenParamList, 'CitizenBlockReasons'>>();
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [isDeleteModalVisible, setDeleteObjectModalVisible] = useState<boolean>(false);
    const [selectedCitizenBlockReason, setSelectedCitizenBlockReason] = useState<CitizenBlockReason | undefined>();

    const [filters, setFilters] = useFilters<CitizenParamList['CitizenBlockReasons']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');

    const queryResult = useQuery({
        queryKey: ['getCitizenBlockReasons', filters],
        queryFn: () => citizenService.getCitizenBlockReasons(filters!),
        enabled: !!authenticatedUser,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }


    function handleFormClose() {
        setIsModalVisible(false);
        setSelectedCitizenBlockReason(undefined);
        refetch();
    }

    function handleDeleteModalClose() {
        setDeleteObjectModalVisible(false);
        setSelectedCitizenBlockReason(undefined);
        refetch();
    }

    async function deleteCitizenBlockReason(id: number) {
        try {
            await citizenService.deleteCitizenBlockReason(id);
            handleDeleteModalClose();
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
        } catch (err) {
            handleDeleteModalClose();
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }


    return (<Container>
        <Header
            title={translate('citizenBlockReasons')}
            actions={<CustomButton text={translate('register')} onPress={() => {
                setIsModalVisible(true);
                setSelectedCitizenBlockReason(undefined);
            }} />}
        />
        <Card>
            <DataTableHeader
                title={translate('registeredReasons')}
                zIndex={2}
                filter={<Filter
                    filters={filters}
                    setFilters={setFilters}
                    textFilter={textFilter}
                    setTextFilter={setTextFilter}
                />}
                filterInput={<FilterInput
                    value={textFilter}
                    placeholder={translate('typeToSearch')}
                    onChange={setTextFilter}
                    onKeyPress={handleKeyDown}
                />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[
                        {
                            name: translate('reason'),
                            cell: row => row.description,
                            grow: 1,
                        },
                        {
                            name: '',
                            button: true,
                            cell: row =>
                                <DropdownButton
                                    size={180}
                                    actions={[{
                                        content: translate('editInformation'),
                                        onPress: () => {
                                            setIsModalVisible(true);
                                            setSelectedCitizenBlockReason(row);
                                        }
                                    },
                                    {
                                        content: translate('deleteCitizenReason'),
                                        variant: 'danger',
                                        onPress: () => {
                                            setDeleteObjectModalVisible(true);
                                            setSelectedCitizenBlockReason(row);
                                        }
                                    }]}
                                />
                        }

                    ]}
                    data={data?.rows || []}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    currentPage={filters.page}
                    totalItems={data?.count || 0}
                    setPage={page => {
                        setFilters(old => ({ ...old, page }));
                        navigation.setParams({ ...route.params, page: page + 1 });
                    }}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({ ...old, limit: pageSize, page: 0 }));
                        navigation.setParams({ ...route.params, limit: pageSize, page: 1 });
                    }}
                />}
            />
        </Card>
        <CitizenBlockReasonModal
            isModalVisible={isModalVisible}
            citizenBlockReasonId={selectedCitizenBlockReason?.id || 0}
            onClose={handleFormClose} />

        {selectedCitizenBlockReason && <DeleteModal
            setModalVisible={setDeleteObjectModalVisible}
            isModalVisible={isDeleteModalVisible}
            itemName={selectedCitizenBlockReason.description}
            onSubmit={() => {
                deleteCitizenBlockReason(selectedCitizenBlockReason.id);
            }}
            onCancel={handleFormClose}
        />}
    </Container>);
}
